import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import TransitionContainer from '../../components/Transition/TranstionContainer';

import { getActionCompletedTime } from '../../utils/actionTiming';
import { CardTypes } from '../../utils/enums';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { copyFor } from '../../copy/copy';

import DetailsFormMultiRepStep2 from '../../components/Email/DetailsFormMultiRepStep2';

import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';

import ToasterAlert from '../../components/Donation/ToasterAlert';
import CssTransition from '../../components/Transition/CssTransition';
import { TransitionGroup } from 'react-transition-group';

import WidgetHeader from '../Widget/WidgetHeader';

import { onGoToNextCard } from '../Widget/actions';
import { cacheFormData } from '../../utils/formCaching/actions';
import { onEmailComplete, onEmailMessageChange, onBackFromStep2 } from './actions';

import { Body, Content, Container, ContentContainer } from '../DefaultStyles/Details';

const MainTitle = styled.h3`
    font-size: 12px;
    line-height: 18px;
    font-weight: normal;
    color: #000;
    margin: -6px 0 12px 0;
`;

const EmailDetails = props => (
    <TransitionContainer
        zIndex={2}
        isSubmitting={props.isSubmitting}
        isBackTransition={props.isBackTransition}
    >
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <TransitionGroup>
                {props.emailSubmitError && (
                    <CssTransition key="alert" in={props.emailSubmitError}>
                        <ToasterAlert content={props.emailSubmitError} />
                    </CssTransition>
                )}
            </TransitionGroup>
            <Body>
                <WidgetHeader />
                <Content>
                    <ContentContainer>
                        <MainTitle>
                            {copyFor('multiRepDetails', 'infoRequired', 'email')}
                        </MainTitle>
                        <DetailsFormMultiRepStep2
                            onSubmit={values => {
                                props.cacheFormData(values);
                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }
                                props.onEmailComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    values,
                                    props.localRepData,
                                    props.isMultiActionsCard,
                                    props.actionIdentifier,
                                    props.thankYouText,
                                    props.thankYouTitle,
                                    props.useLocalRepresentative,
                                    props.recipientMask,
                                    props.repFullName,
                                    props.actionType,
                                    props.emailTopic,
                                    props.emailMessage,
                                    props.localRepresentativeState,
                                    props.localRepDataRepresentatives,
                                    props.skipEndCard
                                );
                            }}
                            response={props.response}
                            onBackFromStep2={() => props.onBackFromStep2()}
                            isSubmitting={props.isSubmitting}
                            isEmailMessageChanged={props.isEmailMessageChanged}
                            onEmailMessageChange={props.onEmailMessageChange}
                        />
                    </ContentContainer>
                </Content>
            </Body>
        </Container>
    </TransitionContainer>
);

EmailDetails.propTypes = {
    nextAction: PropTypes.object,
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    isEmailMessageChanged: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    onEmailComplete: PropTypes.func.isRequired,
    onEmailMessageChange: PropTypes.func.isRequired,
    useLocalRepresentative: PropTypes.bool,
    isMultiActionsCard: PropTypes.bool.isRequired,
    thankYouText: PropTypes.string.isRequired,
    thankYouTitle: PropTypes.string,
    recipientMask: PropTypes.string,
    repFullName: PropTypes.string,
    actionType: PropTypes.number.isRequired,
    emailTopic: PropTypes.string,
    emailMessage: PropTypes.string,
    localRepresentativeState: PropTypes.number,
    emailSubmitError: PropTypes.string,
    isBackTransition: PropTypes.bool,
    cards: PropTypes.array.isRequired,
    skipEndCard: PropTypes.bool,
    onGoToNextCard: PropTypes.func.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    localRepData: PropTypes.object,
    localRepDataRepresentatives: PropTypes.array,
    response: PropTypes.string,
    onBackFromStep2: PropTypes.func.isRequired
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        return {
            nextAction,
            actionCompletedTime: getActionCompletedTime(state, 'email'),
            actionIdentifier: state.widget.activeAction.identifier,
            apiRequestData: state.widget.apiRequestData,
            isEmailMessageChanged: state.email.isEmailMessageChanged,
            isSubmitting: state.email.isSubmitting,
            localRepData: state.localRep.localRepData,
            localRepDataRepresentatives: state.localRep.localRepData.representative
                ? state.localRep.localRepData.representative
                      .filter(function(a) {
                          return state.widget.activeAction.localRepresentativeState === 2
                              ? a.level === 2 &&
                                    a.canBeEmailed &&
                                    (a.fullName !==
                                        state.localRep.hiddenRepresentatives[0] &&
                                        a.fullName !==
                                            state.localRep.hiddenRepresentatives[1])
                              : (a.level === 2 || a.level === 1) &&
                                    a.canBeEmailed &&
                                    (a.fullName !==
                                        state.localRep.hiddenRepresentatives[0] &&
                                        a.fullName !==
                                            state.localRep.hiddenRepresentatives[1] &&
                                        a.fullName !==
                                            state.localRep.hiddenRepresentatives[2]);
                      })
                      .reverse()
                : [],
            response: state.widget.activeAction.text,
            useLocalRepresentative:
                state.widget.activeAction.localRepresentativeState > 0,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            thankYouText: state.widget.activeAction.thankYouText || '',
            thankYouTitle: state.widget.activeAction.thankYouTitle,
            recipientMask: state.widget.activeAction.recipientMask,
            repFullName: state.localRep.localRepData.representative
                ? state.localRep.localRepData.representative[0].fullName
                : '',
            actionType: state.widget.activeAction.localRepresentativeState,
            emailTopic: state.email.topic,
            emailMessage: state.email.message,
            localRepresentativeState: state.widget.activeAction.localRepresentativeState,
            emailSubmitError: state.email.emailSubmitError
                ? state.email.emailSubmitError.message || state.email.emailSubmitError
                : state.email.emailSubmitError,
            isBackTransition: state.email.backButtonClicked,
            cards: state.widget.cards,
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction
        };
    },
    {
        onEmailComplete,
        onEmailMessageChange,
        onBackFromStep2,
        onGoToNextCard,
        handleSkippingEndCard,
        cacheFormData
    }
)(EmailDetails);



// WEBPACK FOOTER //
// ./src/containers/Email/EmailDetailsMultiRepStep2.jsx